import { DndContext, MouseSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import type { Coordinates } from "@dnd-kit/utilities";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import { styled } from "@mui/material/styles";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { restrictToWindowEdgesWithPadding } from "_shared/_views/drag-and-drop/dnd-kit-modifiers";
import Footer from "_shared/_views/footers/footer";
import Header from "_shared/_views/headers/header";
import SideNav from "_shared/_views/side-navs/side-nav";
import AiAssistant from "ai-assistant/_views/ai-assistant";
import Company from "marketplace/companies/company";
import CompanyProfile from "marketplace/companies/profiles/company-profile";
import { addIndividual } from "marketplace/individuals/_stores/individuals-redux-slice";
import Individual from "marketplace/individuals/individual";
import React, { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useSession } from "users/__session/session-context";
import { useGetUserNetworkConnectionsQuery } from "users/networking/connections/_api/user-network-connection-api";

const Root = styled(Box)(() => ({
  alignItems: "stretch",
  display: "flex",
  flexDirection: "row",
  height: "100%",
  overflow: "hidden",
  position: "relative",
  width: "100%",
}));
const MainContainer = styled("main")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(7, 0, 0, 0),
  },
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  overflow: "hidden",
  padding: theme.spacing(8, 0, 0, 0),
  width: "100%",
}));
const PageContent = styled("section")(() => ({
  flexGrow: 1,
  overflow: "hidden",
}));

export default function Main() {
  const assistantRef = React.useRef<HTMLDivElement | null>(null);

  const [defaultCoords, setDefaultCoords] = React.useState<Coordinates>({
    x: globalThis.innerWidth - 200,
    y: globalThis.innerHeight - 100,
  });
  const [chatBubbleCoords, setChatBubbleCoords] = React.useState<Coordinates | null>(null);
  const coords = chatBubbleCoords ?? defaultCoords;

  const [showNav, setShowNav] = React.useState(false);
  const [previousPathname, setPreviousPathname] = React.useState("");

  const session = useSession();
  const dispatch = useAttorneyHubDispatch();
  const location = useLocation();
  const { numDialogs, closeDialog } = useDialog();

  useGetUserNetworkConnectionsQuery(undefined, { skip: !session.isLoggedIn });

  const mouseSensor = useSensor(MouseSensor);
  const touchSensor = useSensor(TouchSensor);
  const sensors = useSensors(mouseSensor, touchSensor);

  // Close all dialogs when the route changes
  useEffect(() => {
    if (location.pathname !== previousPathname && numDialogs > 0) {
      closeDialog();
    }
    setPreviousPathname(location.pathname);
  }, [location.pathname, numDialogs, closeDialog]);

  // On initial mount
  useEffect(() => {
    if (session.user?.individualId && session.user.id && session.user.companyId && session.user.legalEntityId) {
      const userIndividual = Individual.create({
        id: session.user.individualId,
        userId: session.user.id,
        legalEntityId: session.user.legalEntityId,
        isVisible: true,
        profile: {
          userId: session.user.id,
          firstName: session.user.name.firstName ?? "",
          lastName: session.user.name.lastName ?? "",
        },
        company: Company.create({
          id: session.user.companyId,
          legalEntityId: session.user.legalEntityId,
          profile: CompanyProfile.create({
            name: session.user.companyName ?? "",
            categories: [],
          }),
        }),
      });
      dispatch(addIndividual(userIndividual));
    }

    const assistantElement = assistantRef.current;
    if (assistantElement) {
      const { width, height } = assistantElement.getBoundingClientRect();
      setDefaultCoords({
        x: globalThis.innerWidth - width - 16,
        y: globalThis.innerHeight - height - 16,
      });
    }

    function handleWindowResize() {
      const bubbleEl = assistantRef.current;
      if (!bubbleEl) return;

      const { width, height } = bubbleEl.getBoundingClientRect();
      const padding = 16;
      const maxX = globalThis.innerWidth - width - padding;
      const maxY = globalThis.innerHeight - height - padding;

      // Re-calc the "default" bottom-right spot
      setDefaultCoords({ x: maxX, y: maxY });

      // Clamp to client area plus padding
      setChatBubbleCoords((prev) => {
        if (!prev) return null;
        return {
          x: Math.min(Math.max(prev.x, padding), maxX),
          y: Math.min(Math.max(prev.y, padding), maxY),
        };
      });
    }

    globalThis.addEventListener("resize", handleWindowResize);
    handleWindowResize();

    return () => globalThis.removeEventListener("resize", handleWindowResize);
  }, []);

  return (
    <Root>
      <CssBaseline />
      {/* <SystemNotificationBanner /> */}
      <Header
        toggleNavMenuClicked={() => setShowNav(!showNav)}
        hideControls={session.openEntityAgreements.length > 0}
      />
      {!session.openEntityAgreements.length && (
        <>
          <SideNav showNav={showNav} navMenuClosed={() => setShowNav(false)} />
        </>
      )}
      <MainContainer>
        <PageContent>
          <Outlet />
          <DndContext
            sensors={sensors}
            onDragEnd={({ delta }) => {
              setChatBubbleCoords((prev) => {
                const { x, y } = prev ?? defaultCoords;
                return {
                  x: x + delta.x,
                  y: y + delta.y,
                };
              });
            }}
            modifiers={[restrictToWindowEdgesWithPadding(16)]}
          >
            <AiAssistant ref={assistantRef} leftPos={coords.x} topPos={coords.y} />
          </DndContext>
        </PageContent>
        <Footer />
      </MainContainer>
    </Root>
  );
}
